package org.gstechnovos.jexonplastech.sections.contact.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.contact.styles.ContactCardStyle
import org.gstechnovos.jexonplastech.sections.contact.styles.ContactInfoContainerStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.Res
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 *
 * Image Constraints (Icons):
 *
 * Dimensions/Aspect Ratio: The icons are hardcoded to render at exactly 64px by 64px inside
 * a circular box (using a 50% border radius).
 *
 * Constraint: You must upload a perfect 1:1 square icon
 * (SVG format is highly recommended, or a minimum 128x128px PNG)
 * to prevent the image from being distorted or stretching awkwardly within
 * the circular container.
 *
 * Text Constraints:
 * Title: Renders at 20px and bold. Limit to 1–2 words (e.g., "Headquarters", "Direct Phone")
 * to ensure it fits on a single line on smaller desktop screens.
 *
 * Details (detail1 and detail2): Renders at 16px.
 * Limit each detail line to 1 short sentence, a single email address, or a single phone number.
 * Because there are 3 cards side-by-side on desktop, long text strings here will wrap awkwardly or
 * break the card heights.
 *
 */
data class ContactInfoItem(
    val title: String, val detail1: String, val detail2: String, val iconPath: String
)

data class ContactInfoData(
    val cards: List<ContactInfoItem>
)

val defaultContactInfoData = ContactInfoData(
    cards = listOf(
        ContactInfoItem(
            title = "Headquarters",
            detail1 = "123 Manufacturing Zone",
            detail2 = "Rajkot, Gujarat, India",
            iconPath = Res.Image.Contact.HEADQUARTERS
        ), ContactInfoItem(
            title = "Direct Phone",
            detail1 = "+91 98765 43210 (Sales)",
            detail2 = "+91 98765 43211 (Support)",
            iconPath = Res.Image.Contact.PHONE
        ), ContactInfoItem(
            title = "Email Address",
            detail1 = "sales@jexonplastech.com",
            detail2 = "vendor@jexonplastech.com",
            iconPath = Res.Image.Contact.EMAIL
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun ContactInfoSection(data: ContactInfoData = defaultContactInfoData) {
    Box(
        modifier = ContactInfoContainerStyle.toModifier().padding(topBottom = 50.px, leftRight = 5.percent),
        contentAlignment = Alignment.Center
    ) {
        SimpleGrid(
            // 1 column on mobile, 3 columns on desktop
            numColumns(1, md = 3), modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(30.px)
        ) {
            data.cards.forEach { card ->
                ContactCard(card)
            }
        }
    }
}

@Composable
private fun ContactCard(item: ContactInfoItem) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        modifier = ContactCardStyle.toModifier(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        // Icon
        Box(
            modifier = Modifier.size(64.px).margin(bottom = 24.px).borderRadius(50.percent)
                .backgroundColor(palette.brand.primarySoft) // Soft blue background behind the icon
                .padding(16.px), contentAlignment = Alignment.Center
        ) {
            Image(
                src = item.iconPath, alt = item.title, modifier = Modifier.fillMaxSize().unextractable()
            )
        }

        // Title
        SpanText(
            text = item.title,
            modifier = Modifier.color(palette.custom.darkText).fontSize(20.px).fontWeight(FontWeight.Bold)
                .margin(bottom = 16.px).textAlign(TextAlign.Center)
        )

        // Details
        SpanText(
            text = item.detail1,
            modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(16.px)
                .lineHeight(1.6).textAlign(TextAlign.Center).margin(bottom = 4.px)
        )
        SpanText(
            text = item.detail2,
            modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(16.px)
                .lineHeight(1.6).textAlign(TextAlign.Center)
        )
    }
}